// ============================================================================
// StakeTek Fence Height Extensions — SINGLE editable source of truth.
//
// Two SEPARATE products (galvanised + aluminium), each with three length
// variants (450 / 600 / 900 mm). Edit prices, copy and specs HERE, nowhere
// else. The product pages, collection page, JSON-LD, totals and cart lines all
// read from this file.
//
// PROVISIONAL PRICING (set 2026-08-05). These are the launch prices. When a
// price changes here, change the matching Shopify variant price too — Shopify
// is the live checkout, so the two must stay in sync.
//
// LIVE ON SHOPIFY (connected 2026-09-05). Both products are ACTIVE and
// published to the Online Store, Point of Sale, Shop and Buy Button channels,
// so both check out through the normal cart. The product and variant IDs are
// recorded below and in lib/shopify.js SHOPIFY_PRODUCTS. Prices, variant titles
// and weights here match the live Shopify variants exactly — change one and you
// must change the other. See the "Shopify go-live record" at the bottom.
//
// WEIGHTS ARE CONFIRMED (measured 2026-09-05). 495 mm of galvanised sleeve
// weighs 1 kg (2.0202 g/mm) and 600 mm of aluminium weighs 400 g (0.6667 g/mm),
// so aluminium is roughly a third of galvanised for the same length. AU postage
// is weight-based (see lib/shipping.js and the Shopify delivery profile), so
// these values drive what a customer is quoted at checkout.
//
// These are BARE PRODUCT weights, not packed weights. The bracket's Shopify
// weight (550 g in lib/shipping.js) deliberately includes a packaging share on
// top of the bare part. The extensions do not carry that uplift yet, so heavy
// orders may under-quote postage slightly. Decide on a packaging allowance
// before pushing volume through these.
// ============================================================================

// The three lengths every extension is available in. Shared by both products.
export const EXTENSION_LENGTHS_MM = [450, 600, 900];

// Sensible default selection on the product page: the mid length.
export const DEFAULT_EXTENSION_LENGTH_MM = 600;

// Shopify variant title format for a single-option (Length) product.
// A one-option Shopify product names its variants by the option value alone,
// so the 600 mm variant is titled exactly "600 mm". getVariantId() matches on
// this string once the real product is connected.
export const extensionVariantTitle = (mm) => `${mm} mm`;

// The exact compatibility wording — kept practical and non-alarming. Reused on
// both product pages, the collection page and the compatibility FAQ.
export const EXTENSION_COMPATIBILITY_NOTE =
  "Designed to slide over standard, correctly manufactured star pickets. Star picket profiles and dimensions can vary between manufacturers, and some off-spec or poorly manufactured pickets may not fit.";

// How the sleeve gets secured. The pre-drilled holes led people to assume a bolt
// is required; tie wire holds it just as well and is usually what's already in
// the ute. Shown on both product pages under the installation steps, and asked
// and answered in the shared FAQ list below.
export const EXTENSION_FIXING_NOTE =
  "You do not have to bolt it on. Run a bolt through the pre-drilled holes, or tie it on with wire. Use whatever suits the job.";

// Per-material fit line (product.fitNote below), shown directly under the note
// above in the Compatibility box on each product page. The aluminium profile is a
// tighter fit than the galvanised, so a picket whose top has spread from being
// hammered can be a struggle on aluminium and fine on galvanised. Saying so up
// front sends that customer to the right one instead of into a return.

// Core benefit cards — identical on both product pages.
export const EXTENSION_BENEFITS = [
  {
    title: "Use the fence you already have",
    body: "Increase the height of an existing fence without removing and replacing every star picket.",
  },
  {
    title: "Two ways to extend",
    body: "Use the sleeve itself above the existing post, or insert another compatible star picket into the top.",
  },
  {
    title: "Pre-drilled",
    body: "Use the mounting holes to secure the extension and attach additional fencing material, or tie it on with wire instead.",
  },
  {
    title: "No welding required",
    body: "The sleeve is designed for straightforward slide-on mechanical installation.",
  },
  {
    title: "No need to remove your top wires",
    body: "The sleeve slides over the existing picket from the top, so you can raise the fence without taking down the wires already on it.",
  },
  {
    title: "Three useful lengths",
    body: "Choose from 450 mm, 600 mm or 900 mm.",
  },
];

// The two installation methods (headline concept). Shown as a pair on each page.
export const EXTENSION_METHODS = [
  {
    tag: "Method one",
    title: "Use the sleeve as the extension",
    body: "Slide the sleeve over the existing star picket and leave part of it extending above the original post. Use the pre-drilled holes to attach additional wire, mesh or fencing material directly to the extension.",
  },
  {
    tag: "Method two",
    title: "Join another star picket above it",
    body: "Slide the sleeve over the existing star picket, then insert another compatible star picket into the upper end. This lets you substantially increase the total height of the fence.",
  },
];

// Installation overview — the practical, measurement-free walkthrough.
export const EXTENSION_INSTALL_OVERVIEW = [
  {
    title: "Direct extension method",
    body: "Slide the extension over the existing star picket. Position the required amount above the original post and secure it appropriately. Use the pre-drilled holes to attach additional wire, mesh or fencing.",
  },
  {
    title: "Additional-picket method",
    body: "Slide the extension over the existing star picket, then insert another compatible star picket into the upper end. Secure both parts appropriately to create a substantially taller post.",
  },
];

// Intended applications. "Help keep out predators and feral animals" is the
// approved wording — no predator-proof / animal-proof / guaranteed claims.
export const EXTENSION_APPLICATIONS = [
  "Help keep out predators and feral animals",
  "Extending exclusion fencing",
  "Adding additional wire or mesh",
  "Increasing livestock fence height",
  "Increasing garden and property fence height",
  "Joining another star picket above an existing post",
];

// Confirmed specifications shared by both products. The material row is added
// per-product below. Only confirmed facts appear here — no invented weights,
// hole spacing, wall thickness, grades, load ratings or maximum heights.
const SHARED_SPECS = [
  ["Product type", "Star picket fence height extension sleeve"],
  ["Available lengths", "450 mm, 600 mm and 900 mm"],
  ["Installation", "Slides over an existing star picket"],
  ["Secondary use", "Can join another compatible star picket above the existing post"],
  ["Fixing", "Pre-drilled mounting holes, or tie wire"],
  ["Welding required", "No"],
  ["Intended applications", "Additional wire, mesh and exclusion fencing"],
];

// FAQs shared by both products, in display order. Material-specific FAQs are
// appended per-product below.
const SHARED_FAQS = [
  {
    q: "What does the StakeTek Fence Height Extension fit?",
    a: "It is designed to slide over standard, correctly manufactured star pickets. Profiles and dimensions can vary slightly between manufacturers.",
  },
  {
    q: "Can I place another star picket into the top?",
    a: "Yes. The sleeve can be used to join another compatible star picket above the existing post.",
  },
  {
    q: "Can I use the extension without adding another picket?",
    a: "Yes. The pre-drilled sleeve can extend above the original picket and support additional wire, mesh or fencing material.",
  },
  {
    q: "What lengths are available?",
    a: "The extensions are available in 450 mm, 600 mm and 900 mm lengths.",
  },
  {
    q: "Does installation require welding?",
    a: "No. The product is designed for slide-on mechanical installation.",
  },
  {
    q: "Do I have to bolt the extension on?",
    a: "No. Run a bolt through the pre-drilled holes if you want to, or tie it on with wire. Both hold it in place, so use whatever suits the job.",
  },
  {
    q: "Will it fit every star picket?",
    a: "It is designed for standard star pickets, but dimensions can vary between manufacturers. Some off-spec or poorly manufactured pickets may not fit.",
  },
];

// Weights differ by material and length, so this FAQ is built per product from
// the variant weights rather than sitting in the shared list.
const weightFaq = (product) => ({
  q: "How much does it weigh?",
  a: `Approximate weights: ${product.variants
    .map((v) => `${v.mm} mm is ${(v.weightGrams / 1000).toFixed(2)} kg`)
    .join(", ")}.`,
});

// Linear weight of each profile, from a measured sample of each. Aluminium
// lands at about a third of galvanised for the same length, which is what the
// steel-to-aluminium density difference predicts.
// Galvanised ships at its packed weight (Tanner, 2026-10-08), set per length
// after a 25 x 900 mm order to WA weighed in light at the bare 495 mm = 1 kg
// rate: 900 mm = 2 kg, 600 mm = 1.5 kg, 450 mm on the same line = 1.25 kg.
const GALVANISED_PACKED_GRAMS = { 450: 1250, 600: 1500, 900: 2000 };
const ALUMINIUM_G_PER_MM = 400 / 600;   // measured: 600 mm = 400 g

// Build a product's variant list from the length + price + variant-id maps.
// weightGrams comes from the profile's linear weight (or a per-length map) and
// MUST match the weight set on the matching Shopify variant, since Shopify
// charges postage off it: galvanised 1250 / 1500 / 2000 g, aluminium 300 / 400 / 600 g.
//
// `sku` is the same code carried on the Shopify variant AND in the Google
// Merchant feed (public/google-feed.xml) as g:id / g:mpn. These parts have no
// GTIN, so that code is the only identifier tying the three together — change it
// in one place and you must change it in all three.
const buildVariants = (priceByLength, variantIdByLength, weight, skuPrefix) =>
  EXTENSION_LENGTHS_MM.map((mm) => ({
    mm,
    price: priceByLength[mm],
    shopifyVariantId: variantIdByLength[mm],
    weightGrams: typeof weight === "number" ? Math.round(mm * weight) : weight[mm],
    sku: `${skuPrefix}-${String(mm).padStart(4, "0")}`,
  }));

export const EXTENSIONS = {
  galvanised: {
    key: "galvanised",
    slug: "galvanised-fence-height-extension",
    path: "/products/galvanised-fence-height-extension",
    name: "StakeTek Galvanised Fence Height Extension",
    shortName: "Galvanised Fence Height Extension",
    productLabel: "STAKETEK · FENCE HEIGHT EXTENSION",
    badge: "GALVANISED STEEL",
    material: "Galvanised steel",
    // Concise material description for the collection card.
    cardDescription: "Substantial galvanised steel. The heavier-duty traditional option.",
    heroTitle: "StakeTek Galvanised Fence Height Extension",
    heroDescription:
      "Extend the height of an existing star picket fence without replacing the posts. Use the pre-drilled galvanised sleeve as the extension itself, or insert another star picket above it for considerably greater height.",
    supportingStatement:
      "A substantial galvanised steel solution for adding extra wire, mesh or exclusion fencing to an existing star picket fence.",
    positioning: [
      "The heavier-duty traditional option",
      "Substantial galvanised steel construction",
      "Durable and practical for rural fencing",
      "Made using the same sleeve profile used in StakeTek brackets",
      "Suitable for additional wire, mesh and exclusion fencing",
      "Suitable for use as a joining sleeve for another star picket",
      "Pre-drilled",
      "No welding required",
    ],
    // Material-specific information block shown on the product page.
    materialInfo: {
      heading: "About galvanised steel",
      body: "Galvanised steel provides a substantial, traditional fencing option suited to rural and general-purpose applications. It is the heavier-duty choice in the range and uses the same substantial sleeve profile as StakeTek slide-on brackets.",
    },
    fitNote:
      "The galvanised is the more forgiving fit of the two. If a picket has been hammered and the top has spread at all, this is the one to use.",
    // Card-length version of fitNote for the collection page, where the two
    // materials sit side by side and the choice actually gets made.
    cardFitNote: "Fit: more forgiving on older pickets",
    // Cross-link to the alternative material.
    crossLink: {
      text: "Looking for a lighter option?",
      label: "View the aluminium extension",
      to: "/products/aluminium-fence-height-extension",
    },
    shopifyProductId: "9257503653942",
    // Real product photos (shot 2026-08-06). Galvanised sleeve slid over a star
    // picket in the field, showing the pre-drilled holes and the joiner in use.
    images: [
      { src: "/images/staketek-fence-extension-detail.jpg", alt: "StakeTek galvanised fence extension sleeve over a star picket, showing the pre-drilled holes and a second picket joined into the top" },
      { src: "/images/staketek-fence-extension-installed-1.jpg", alt: "Two StakeTek galvanised fence extensions raising a star picket fence line in a paddock" },
      { src: "/images/staketek-fence-extension-installed-2.jpg", alt: "StakeTek galvanised fence extension on a star picket in a rural fence line" },
      { src: "/images/staketek-fence-extension-detail-2.jpg", alt: "Close view of the StakeTek galvanised fence extension sleeve and its pre-drilled mounting holes" },
      { src: "/images/staketek-fence-extension-installed-3.jpg", alt: "StakeTek galvanised fence extension raising fence height on a star picket" },
      { src: "/images/staketek-fence-extension-installed-4.jpg", alt: "StakeTek galvanised fence extension sleeve on a star picket in a paddock fence line" },
    ],
    // Per-length prices (AUD, each) — in sync with the live Shopify variants.
    // Raised $1.50 across the board on 2026-09-08.
    variants: buildVariants(
      { 450: 8.35, 600: 9.0, 900: 11.0 },
      { 450: "47579620606006", 600: "47629344178230", 900: "47629344210998" },
      GALVANISED_PACKED_GRAMS,
      "EXT-GAL"
    ),
    // Title leads with what people type ("star picket extension") rather than the
    // internal product name, and carries the two facts that decide the click:
    // the material and the length range.
    seoTitle: "Galvanised Star Picket Extension | Make a Fence Taller 450–900 mm",
    seoDescription:
      "Raise an existing star picket fence without pulling out the posts. Pre-drilled galvanised slide-over extension sleeve in 450, 600 and 900 mm, from $8.35 each. Australian made, free VIC pickup.",
    keywords:
      "galvanised star picket extension, star picket extension sleeve, fence height extension, extend star picket fence, make fence taller, raise fence height, star picket joiner, star picket joining sleeve, galvanised fence extension sleeve, fence extension post, add height to existing fence, exclusion fence extension",
    materialFaq: {
      q: "Why choose galvanised steel?",
      a: "Galvanised steel provides a substantial, traditional fencing option suited to rural and general-purpose applications.",
    },
  },

  aluminium: {
    key: "aluminium",
    slug: "aluminium-fence-height-extension",
    path: "/products/aluminium-fence-height-extension",
    name: "StakeTek Aluminium Fence Height Extension",
    shortName: "Aluminium Fence Height Extension",
    productLabel: "STAKETEK · FENCE HEIGHT EXTENSION",
    badge: "ALUMINIUM",
    material: "Aluminium",
    cardDescription: "Lightweight and corrosion-resistant. Easier to carry along long fence lines.",
    heroTitle: "StakeTek Aluminium Fence Height Extension",
    heroDescription:
      "Extend the height of an existing star picket fence with a lightweight, corrosion-resistant sleeve. Attach additional wire or mesh directly to the extension, or insert another star picket above it for considerably greater height.",
    supportingStatement:
      "A lightweight and practical solution for increasing fence height without replacing the existing posts.",
    positioning: [
      "Lightweight",
      "Corrosion-resistant",
      "Easier to carry and handle",
      "Practical for long fence lines",
      "Made using the StakeTek star-picket sleeve profile",
      "Suitable for additional wire, mesh and exclusion fencing",
      "Suitable for joining another star picket above the original post",
      "Pre-drilled",
      "No welding required",
    ],
    materialInfo: {
      heading: "About aluminium",
      body: "Aluminium is lightweight, corrosion-resistant and easier to carry and handle across longer fence lines. It uses the same StakeTek star-picket sleeve profile, so it installs the same slide-on way as the galvanised option.",
    },
    fitNote:
      "The aluminium is a tighter fit than the galvanised and suits new or near-new star pickets. If the picket has been hammered and the top has spread at all, the galvanised version goes on easier.",
    cardFitNote: "Fit: tighter, suits new or near-new pickets",
    // Shown beside the buy box, where the choice is made, not only further
    // down in Compatibility (Tanner, 2026-10-08).
    buyNote: {
      text: "Aluminium has a tighter fit than the galvanised, so it suits new or near-new star pickets. If your pickets have been hammered or the tops have spread, choose the",
      linkLabel: "galvanised extension",
      linkTo: "/products/galvanised-fence-height-extension",
    },
    crossLink: {
      text: "Prefer a substantial galvanised steel option?",
      label: "View the galvanised extension",
      to: "/products/galvanised-fence-height-extension",
    },
    shopifyProductId: "9285191827510",
    // Photos: reusing two of the galvanised in-field shots for now (approved by
    // Tanner) until aluminium-specific photography is available. The same two are
    // attached to the live Shopify product, so swap them in both places.
    images: [
      { src: "/images/staketek-fence-extension-installed-1.jpg", alt: "StakeTek fence extension raising a star picket fence line in a paddock" },
      { src: "/images/staketek-fence-extension-detail.jpg", alt: "StakeTek fence extension sleeve over a star picket, showing the pre-drilled mounting holes" },
    ],
    // Per-length prices (AUD, each) — in sync with the live Shopify variants.
    variants: buildVariants(
      { 450: 6.45, 600: 6.95, 900: 8.95 },
      { 450: "47629346144310", 600: "47629346177078", 900: "47629346209846" },
      ALUMINIUM_G_PER_MM,
      "EXT-ALU"
    ),
    // The aluminium's real search advantage is weight: it's a third of the
    // galvanised, which is what someone walking a long fence line is looking for.
    seoTitle: "Aluminium Star Picket Extension | Lightweight Fence Height Sleeve",
    seoDescription:
      "Raise an existing star picket fence with a lightweight, corrosion-resistant slide-over sleeve. Pre-drilled, in 450, 600 and 900 mm, from $6.45 each. A third the weight of galvanised for long fence lines.",
    keywords:
      "aluminium star picket extension, star picket extension sleeve, fence height extension, extend star picket fence, make fence taller, raise fence height, lightweight fence extension, star picket joiner, star picket joining sleeve, aluminium fence extension sleeve, add height to existing fence, exclusion fence extension",
    materialFaq: {
      q: "Why choose aluminium?",
      a: "Aluminium is lightweight, corrosion-resistant and easier to carry and handle across longer fence lines.",
    },
  },
};

// Order the collection presents the two products in.
export const EXTENSION_KEYS = ["galvanised", "aluminium"];

// Per-product derived helpers -------------------------------------------------

// Full spec table for a product = shared specs + its material row.
export const extensionSpecs = (product) => [...SHARED_SPECS, ["Material", product.material]];

// Full FAQ list for a product = shared FAQs + weights + its material FAQ.
export const extensionFaqs = (product) => [...SHARED_FAQS, weightFaq(product), product.materialFaq];

// Unit price for a selected length on a product.
export const extensionUnitPrice = (product, mm) =>
  product.variants.find((v) => v.mm === Number(mm))?.price ?? product.variants[0].price;

// Lowest ("from") price for a product — used on cards and JSON-LD.
export const extensionFromPrice = (product) =>
  Math.min(...product.variants.map((v) => v.price));

// Heaviest order the "Fence Extensions" delivery profile still prices
// automatically: AusPost at cost, then a $550 pallet per 500 kg up to 5,000 kg.
// Above it there is no rate, so those orders are quoted per job. Keep this in
// step with EXTENSION_MAX_SHIP_KG in lib/shipping.js and the live profile.
export const MAX_ONLINE_SHIP_KG = 5000;

// Shipping weight of a given selection, in kg.
export const extensionOrderWeightKg = (product, mm, qty) => {
  const variant = product.variants.find((v) => v.mm === Number(mm));
  return ((variant?.weightGrams ?? 0) * Math.max(1, Number(qty) || 1)) / 1000;
};

// True when the order is too heavy to check out and needs a freight quote.
export const extensionNeedsFreightQuote = (product, mm, qty) =>
  extensionOrderWeightKg(product, mm, qty) > MAX_ONLINE_SHIP_KG;

// Largest quantity of a length that still checks out online.
export const extensionMaxOnlineQty = (product, mm) => {
  const variant = product.variants.find((v) => v.mm === Number(mm));
  if (!variant?.weightGrams) return Infinity;
  return Math.floor((MAX_ONLINE_SHIP_KG * 1000) / variant.weightGrams);
};

// True once the product has a live Shopify product ID. The cart resolves each
// variant by its title ("300 mm" etc.), so a product ID is all that's needed to
// go live — variant IDs are optional documentation. Until an ID is set, the page
// shows the "checkout being finalised" state.
export const extensionIsBuyable = (product) => Boolean(product.shopifyProductId);

// ----------------------------------------------------------------------------
// SHOPIFY GO-LIVE RECORD (Fence Height Extensions) — done 2026-09-05
// ----------------------------------------------------------------------------
// Galvanised — product 9257503653942 (handle staketek-galvanised-fence-height-extension)
//   450 mm  47579620606006  $8.35  0.909 kg  SKU EXT-GAL-0450
//   600 mm  47629344178230  $9.00  1.212 kg  SKU EXT-GAL-0600
//   900 mm  47629344210998  $11.00 1.818 kg  SKU EXT-GAL-0900
// Aluminium — product 9285191827510 (handle staketek-aluminium-fence-height-extension)
//   450 mm  47629346144310  $6.45  0.300 kg  SKU EXT-ALU-0450
//   600 mm  47629346177078  $6.95  0.400 kg  SKU EXT-ALU-0600
//   900 mm  47629346209846  $8.95  0.600 kg  SKU EXT-ALU-0900
//
// Both products: ONE option "Length" with variant titles "450 mm" / "600 mm" /
// "900 mm" (these must keep matching extensionVariantTitle(), which is how the
// cart resolves a variant). Status ACTIVE, published to Online Store, Point of
// Sale, Shop and Buy Button. Inventory UNTRACKED with policy CONTINUE, matching
// the Flat Slide-On Bracket, so a sale is never blocked by a stock count.
//
// If you change anything here, change it on the Shopify variant too:
//   · price      — the site shows this number, Shopify charges it
//   · weight     — Shopify quotes postage from it (lib/shipping.js tiers)
//   · length set — EXTENSION_LENGTHS_MM drives both the buttons and the lookup
//
// No bulk discount applies to the extensions. The live Shopify automatic
// discounts are scoped to the brackets only (see lib/pricing.js), and Shopify
// applies just one automatic discount per order.
